import { useContext, useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { AppContext } from "@/App";
import api, { loginWithGoogle } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Crown, LogOut, Loader2, Pencil, XCircle, User } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { t, lang, setUser, user } = useContext(AppContext);
  const location = useLocation();
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(null);

  const reloadBookings = async () => {
    const res = await api.get("/my-bookings");
    setBookings(res.data);
  };

  const startEdit = (b) => {
    setEditing(b.booking_id);
    setEditForm({ pickup: b.pickup, dropoff: b.dropoff, vehicle: b.vehicle, date: b.date, time: b.time, passengers: b.passengers });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/bookings/${editing}`, { ...editForm, passengers: Number(editForm.passengers) });
      toast.success(t.account.updatedOk);
      setEditing(null);
      await reloadBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const cancelBooking = async (id) => {
    try {
      const res = await api.post(`/bookings/${id}/cancel`);
      toast.success(t.account.cancelledOk + (res.data.refunded ? ` · ${t.account.refundedNote}` : ""));
      setConfirmCancel(null);
      await reloadBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      return;
    }
    (async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        setAuthState(true);
      } catch {
        setAuthState(false);
      }
    })();
  }, [location.state, setUser]);

  useEffect(() => {
    if (!authState) return;
    setLoadingBookings(true);
    api.get("/my-bookings")
      .then((res) => setBookings(res.data))
      .catch(() => {})
      .finally(() => setLoadingBookings(false));
  }, [authState]);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    setAuthState(false);
  };

  const statusLabel = (b) => {
    if (b.status === "cancelled") return lang === "it" ? "Annullata" : "Cancelled";
    if (b.payment_status === "refunded") return lang === "it" ? "Rimborsata" : "Refunded";
    if (b.payment_status === "paid") return t.account.paid;
    if (b.payment_method === "onboard") return t.account.payOnboard;
    return t.account.pendingPayment;
  };

  const statusColor = (b) => {
    if (b.status === "cancelled" || b.payment_status === "refunded") return "text-red-400";
    if (b.payment_status === "paid") return "text-emerald-400";
    return "text-amber-400";
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Header />
      <main className="mx-auto max-w-5xl px-4 pb-24 pt-32 sm:px-6" data-testid="account-page">
        {authState === null && (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
          </div>
        )}

        {authState === false && (
          <div className="card-surface mx-auto max-w-md p-10 text-center" data-testid="account-login-prompt">
            <Crown className="mx-auto mb-4 h-10 w-10 text-[#D4AF37]" />
            <h1 className="font-display text-3xl font-semibold text-white">{t.account.title}</h1>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">{t.account.loginPrompt}</p>
            <button data-testid="account-google-login-btn" onClick={loginWithGoogle} className="btn-gold mt-8 w-full">
              {t.nav.login}
            </button>
          </div>
        )}

        {authState === true && user && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {user.picture && <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-[rgba(212,175,55,0.4)]" />}
                <div>
                  <p className="label-accent !text-[10px]">{t.account.welcome}</p>
                  <h1 className="font-display text-3xl font-semibold text-white" data-testid="account-user-name">{user.name}</h1>
                  <p className="text-xs text-neutral-500">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user.is_admin && (
                  <Link to="/admin" data-testid="admin-link" className="btn-gold !px-4 !py-2 !text-xs">
                    Gestione corse
                  </Link>
                )}
                <button data-testid="logout-btn" onClick={logout} className="btn-outline-gold !px-4 !py-2 !text-xs">
                  <LogOut className="h-3.5 w-3.5" /> {t.account.logout}
                </button>
              </div>
            </div>

            <h2 className="label-accent mt-14">{t.account.myBookings}</h2>
            {loadingBookings && <p className="mt-6 text-sm text-neutral-500">{t.account.loading}</p>}
            {!loadingBookings && bookings.length === 0 && (
              <p className="mt-6 text-sm text-neutral-400" data-testid="no-bookings-message">{t.account.noBookings}</p>
            )}
            <div className="mt-6 space-y-3">
              {bookings.map((b) => (
                <div key={b.booking_id} data-testid={`booking-row-${b.booking_id}`} className="card-surface p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-lg font-semibold text-white">{b.pickup} → {b.dropoff}</p>
                      <p className="mt-1 text-xs text-neutral-500">{b.date} · {b.time} · {t.vehicles[b.vehicle]?.name || b.vehicle}</p>
                      {b.driver_name && (
                        <p data-testid={`driver-info-${b.booking_id}`} className="mt-1.5 flex items-center gap-1.5 text-xs text-[#E5C158]">
                          <User className="h-3.5 w-3.5" />
                          {t.account.yourDriver}: <span className="font-medium">{b.driver_name}</span>
                          {b.driver_phone && (
                            <a href={`tel:${b.driver_phone}`} className="underline underline-offset-2">· {b.driver_phone}</a>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl font-semibold text-[#E5C158]">€{b.amount.toFixed(2)}</p>
                      <p className={`mt-0.5 font-code text-[10px] uppercase tracking-widest ${statusColor(b)}`}>
                        {statusLabel(b)}
                      </p>
                    </div>
                  </div>
                  {(b.status === "confirmed" || b.status === "pending_payment") && (
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[rgba(212,175,55,0.15)] pt-4">
                      {b.payment_status === "paid" ? (
                        <p className="text-xs text-neutral-500">{t.account.paidLocked}</p>
                      ) : (
                        <button
                          data-testid={`edit-${b.booking_id}`}
                          onClick={() => (editing === b.booking_id ? setEditing(null) : startEdit(b))}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(212,175,55,0.4)] px-4 py-1.5 text-xs font-semibold text-[#E5C158] transition-colors hover:bg-[rgba(212,175,55,0.08)]"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {editing === b.booking_id ? t.account.closeEdit : t.account.edit}
                        </button>
                      )}
                      {confirmCancel === b.booking_id ? (
                        <button
                          data-testid={`confirm-cancel-${b.booking_id}`}
                          onClick={() => cancelBooking(b.booking_id)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-red-500 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-400"
                        >
                          <XCircle className="h-3.5 w-3.5" /> {t.account.confirmCancel}
                        </button>
                      ) : (
                        <button
                          data-testid={`cancel-${b.booking_id}`}
                          onClick={() => setConfirmCancel(b.booking_id)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-900/60 px-4 py-1.5 text-xs font-semibold text-red-400 transition-colors hover:bg-red-950/40"
                        >
                          <XCircle className="h-3.5 w-3.5" /> {t.account.cancelBooking}
                        </button>
                      )}
                    </div>
                  )}
                  {editing === b.booking_id && editForm && (
                    <div data-testid={`edit-form-${b.booking_id}`} className="mt-4 grid gap-3 border-t border-[rgba(212,175,55,0.15)] pt-4 sm:grid-cols-2">
                      <input data-testid="edit-pickup" className="input-dark" value={editForm.pickup} onChange={(e) => setEditForm({ ...editForm, pickup: e.target.value })} />
                      <input data-testid="edit-dropoff" className="input-dark" value={editForm.dropoff} onChange={(e) => setEditForm({ ...editForm, dropoff: e.target.value })} />
                      <input data-testid="edit-date" type="date" className="input-dark [color-scheme:dark]" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
                      <input data-testid="edit-time" type="time" className="input-dark [color-scheme:dark]" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} />
                      <select data-testid="edit-vehicle" className="input-dark" value={editForm.vehicle} onChange={(e) => setEditForm({ ...editForm, vehicle: e.target.value })}>
                        {["eclass", "sclass", "vclass"].map((v) => <option key={v} value={v}>{t.vehicles[v].name}</option>)}
                      </select>
                      <select data-testid="edit-passengers" className="input-dark" value={editForm.passengers} onChange={(e) => setEditForm({ ...editForm, passengers: e.target.value })}>
                        {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <button data-testid={`save-edit-${b.booking_id}`} onClick={saveEdit} disabled={saving} className="btn-gold sm:col-span-2">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t.account.save}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Link to="/" className="btn-gold mt-10">{t.hero.cta}</Link>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
